/**
 * PaginatedList - Component for classic pagination with page numbers
 */

import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Stack, Loader, Text, Center, Button, Group, Pagination, Select, Box } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

export interface PaginatedListProps<T> {
  queryKey: unknown[]
  fetchFn: (page: number, limit: number) => Promise<{ data: T[]; total?: number; pagination?: { total: number } }>
  renderItem: (item: T, index: number) => React.ReactNode
  getItemKey?: (item: T, index: number) => string | number
  pageSize?: number
  pageSizeOptions?: number[]
  enabled?: boolean
  emptyMessage?: string
  emptyIcon?: React.ReactNode
  showPageInfo?: boolean
  showPageSizeSelector?: boolean
  gap?: string | number
  className?: string
}

export function PaginatedList<T>({
  queryKey,
  fetchFn,
  renderItem,
  getItemKey,
  pageSize: initialPageSize = 20,
  pageSizeOptions = [10, 20, 50],
  enabled = true,
  emptyMessage,
  emptyIcon,
  showPageInfo = true,
  showPageSizeSelector = false,
  gap = 'md',
  className,
}: PaginatedListProps<T>) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  
  const queryKeyRef = useRef(JSON.stringify(queryKey))
  useEffect(() => {
    const newKey = JSON.stringify(queryKey)
    if (queryKeyRef.current !== newKey) {
      queryKeyRef.current = newKey
      setPage(1)
    }
  }, [queryKey])

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: [...queryKey, { page, pageSize }],
    queryFn: async () => {
      const offset = (page - 1) * pageSize
      const result = await fetchFn(offset, pageSize)
      return {
        data: result.data,
        total: result.total ?? result.pagination?.total ?? result.data.length,
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  })

  const items = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / pageSize)
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, total)

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePageSizeChange = (value: string | null) => {
    if (value) {
      setPageSize(parseInt(value))
      setPage(1)
    }
  }

  if (isLoading && !data) {
    return (
      <Center style={{ minHeight: '200px' }}>
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text c="dimmed">{t('common.loading')}</Text>
        </Stack>
      </Center>
    )
  }

  if (isError) {
    return (
      <Center style={{ minHeight: '200px' }}>
        <Stack align="center" gap="sm">
          <Text c="red">{t('common.error')}</Text>
          {error && <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>{error instanceof Error ? error.message : 'Unknown error'}</Text>}
          <Button leftSection={<IconRefresh size={16} />} onClick={() => refetch()} variant="light">
            {t('common.retry')}
          </Button>
        </Stack>
      </Center>
    )
  }

  if (items.length === 0 && !isFetching) {
    return (
      <Center style={{ minHeight: '200px' }}>
        <Stack align="center" gap="sm">
          {emptyIcon}
          <Text c="dimmed" ta="center">{emptyMessage || t('common.no_items')}</Text>
        </Stack>
      </Center>
    )
  }

  return (
    <div className={className}>
      <Stack gap={gap}>
        {(showPageInfo || showPageSizeSelector) && (
          <Group justify="space-between" wrap="wrap">
            {showPageInfo && (
              <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>
                {t('common.showing_items', { start: startItem, end: endItem, total })}
              </Text>
            )}
            {showPageSizeSelector && (
              <Group gap="xs">
                <Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>{t('common.per_page')}:</Text>
                <Select
                  size="xs"
                  value={pageSize.toString()}
                  onChange={handlePageSizeChange}
                  data={pageSizeOptions.map(size => ({ value: size.toString(), label: size.toString() }))}
                  style={{ width: 80 }}
                />
              </Group>
            )}
          </Group>
        )}

        <Stack gap={gap} style={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          {items.map((item, index) => {
            const key = getItemKey ? getItemKey(item, index) : index
            return <div key={key}>{renderItem(item, index)}</div>
          })}
        </Stack>

        {totalPages > 1 && (
          <Box mt="md" style={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              value={page}
              onChange={handlePageChange}
              total={totalPages}
              siblings={0}
              boundaries={1}
              disabled={isFetching}
            />
          </Box>
        )}

        {isFetching && data && (
          <Center py="sm"><Loader size="sm" /></Center>
        )}
      </Stack>
    </div>
  )
}

export function usePaginatedList<T>(
  queryKey: unknown[],
  fetchFn: (offset: number, limit: number) => Promise<{ data: T[]; total?: number; pagination?: { total: number } }>,
  options?: { pageSize?: number; enabled?: boolean }
) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(options?.pageSize ?? 20)

  const query = useQuery({
    queryKey: [...queryKey, { page, pageSize }],
    queryFn: async () => {
      const offset = (page - 1) * pageSize
      const result = await fetchFn(offset, pageSize)
      return {
        data: result.data,
        total: result.total ?? result.pagination?.total ?? result.data.length,
      }
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  })

  const items = query.data?.data ?? []
  const total = query.data?.total ?? 0
  const totalPages = Math.ceil(total / pageSize)

  return { ...query, items, total, page, pageSize, totalPages, setPage, setPageSize }
}
